import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Refresh,
  Search,
  Edit,
  Visibility,
  AccountBalance,
  Euro,
  CalendarToday,
  TrendingUp,
  TrendingDown,
  Business,
  Assessment,
  CheckCircle,
  ArrowForward,
  People,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import LimitService from '../../services/limitService';
import useRoleColors from '../../hooks/useRoleColors';

const ViewLimits = ({ user }) => {
  const {
    accent,
    accentLight,
    accentDark,
    accentGradient,
    accentGradientHover,
    softBg,
    softBgHover,
    softBgStrong,
    contrast,
    outline,
  } = useRoleColors();
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLimit, setSelectedLimit] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, limit: null });
  const [editForm, setEditForm] = useState({
    amount: '',
    currency: 'EUR',
    depositAccountNumber: '',
    validityDate: null,
  });

  // Fetch all limits
  const fetchLimits = async () => {
    setLoading(true);
    setError('');

    const result = await LimitService.getAllLimits();
    
    if (result.success) {
      setLimits(result.data);
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  // Format currency
  const formatCurrency = (amount, currency = 'EUR') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    const parts = formatter.formatToParts(amount);
    let formatted = '';
    
    for (const part of parts) {
      if (part.type === 'currency') {
        formatted += part.value + ' ';
      } else {
        formatted += part.value;
      }
    }
    
    return formatted.trim();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return 'success';
      case 0:
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return 'Active';
      case 0:
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  // Filter limits based on search term
  const getFilteredLimits = () => {
    if (!searchTerm) return limits;
    
    const term = searchTerm.toLowerCase();
    return limits.filter(limit => 
      limit.userId.toString().includes(term) ||
      limit.userName.toLowerCase().includes(term) ||
      limit.currency.toLowerCase().includes(term)
    );
  };

  // Calculate totals
  const getTotals = () => {
    const filtered = getFilteredLimits();
    const totalAmount = filtered.reduce((sum, limit) => sum + limit.amount, 0);
    const totalRemaining = filtered.reduce((sum, limit) => sum + limit.remainingAmount, 0);
    const totalUsed = totalAmount - totalRemaining;
    const uniqueCustomers = new Set(filtered.map(limit => limit.userId));
    const totalCustomer = uniqueCustomers.size;
    
    return { totalAmount, totalRemaining, totalUsed, totalCustomer };
  };

  // Handle edit limit
  const handleEditLimit = (limit) => {
    setEditForm({
      amount: limit.amount.toString(),
      currency: limit.currency,
      depositAccountNumber: limit.depositAccountNumber.toString(),
      validityDate: new Date(limit.validityDate),
    });
    setEditDialog({ open: true, limit });
  };

  // Handle edit form change
  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Format currency input
  const formatCurrencyInput = (value) => {
    const numericValue = value.replace(/[^\d.]/g, '');
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  const handleAmountChange = (value) => {
    const formatted = formatCurrencyInput(value);
    handleEditFormChange('amount', formatted);
  };

  // Submit edit
  const handleEditSubmit = async () => {
    if (!editDialog.limit) return;

    const formattedDate = editForm.validityDate instanceof Date 
      ? editForm.validityDate.toISOString().split('T')[0]
      : editForm.validityDate;

    // Convert currency string to enum value
    const currencyEnum = editForm.currency === 'TRY' ? 1 : 
                        editForm.currency === 'USD' ? 2 : 
                        editForm.currency === 'EUR' ? 3 : 
                        editForm.currency === 'GBP' ? 4 : 1; // Default to TRY

    const updateData = {
      userId: editDialog.limit.userId,
      amount: parseFloat(editForm.amount),
      currency: currencyEnum,
      depositAccountNumber: parseInt(editForm.depositAccountNumber),
      validityDate: formattedDate
    };

    const result = await LimitService.updateLimit(updateData);
    
    if (result.success) {
      setEditDialog({ open: false, limit: null });
      await fetchLimits();
      toast.success('Limit successfully updated!');
    } else {
      toast.error(result.message);
    }
  };

  // Close edit dialog
  const closeEditDialog = () => {
    setEditDialog({ open: false, limit: null });
    setEditForm({
      amount: '',
      currency: 'EUR',
      depositAccountNumber: '',
      validityDate: null,
    });
  };

  // Initialize
  useEffect(() => {
    fetchLimits();
  }, []);

  const filteredLimits = getFilteredLimits();
  const totals = getTotals();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: accentGradient, 
            p: 3, 
            color: 'white' 
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{
                  width: 64,
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <AccountBalance sx={{ fontSize: 32 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  View Line of Credit
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  View and manage customer line of credits
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Refresh />}
                  onClick={fetchLimits}
                  disabled={loading}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>

            {/* Summary Cards */}
            <Box sx={{ 
              mb: 4,
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(4, 1fr)' 
              },
              gap: 3,
              gridAutoRows: '1fr'
            }}>
          <Card elevation={8} sx={{ 
            borderRadius: 4,
            transition: 'all 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 3, 
                    backgroundColor: softBg,
                    mr: 2
                  }}>
                    <AccountBalance sx={{ color: accent, fontSize: 28 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Total Limits
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: accent, mb: 1 }}>
                  {formatCurrency(totals.totalAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredLimits.length} active limits
                </Typography>
              </CardContent>
            </Card>

          <Card elevation={8} sx={{ 
            borderRadius: 4,
            transition: 'all 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 3, 
                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  mr: 2
                }}>
                  <People sx={{ color: '#9c27b0', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Total Customer
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0', mb: 1 }}>
                {totals.totalCustomer}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totals.totalCustomer === 1 ? 'customer' : 'customers'} with limits
              </Typography>
            </CardContent>
          </Card>
          
          <Card elevation={8} sx={{ 
            borderRadius: 4,
            transition: 'all 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 3, 
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  mr: 2
                }}>
                  <TrendingUp sx={{ color: 'success.main', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Available
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                {formatCurrency(totals.totalRemaining)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {((totals.totalRemaining / totals.totalAmount) * 100 || 0).toFixed(1)}% available
              </Typography>
            </CardContent>
          </Card>
          
          <Card elevation={8} sx={{ 
            borderRadius: 4,
            transition: 'all 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 3, 
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  mr: 2
                }}>
                  <TrendingDown sx={{ color: 'warning.main', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Used
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
                {formatCurrency(totals.totalUsed)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {((totals.totalUsed / totals.totalAmount) * 100 || 0).toFixed(1)}% utilized
              </Typography>
            </CardContent>
          </Card>
        </Box>

            {/* Search */}
            <Box sx={{ mb: 3, p: 3, backgroundColor: 'grey.50', borderRadius: 3 }}>
              <TextField
                fullWidth
                placeholder="Search by Customer ID, Name, or Currency..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '&:hover fieldset': { borderColor: accent },
                    '&.Mui-focused fieldset': { borderColor: accent }
                  }
                }}
              />
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Limits Table */}
            <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Customer ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Customer Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Total Limit</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Available</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Used</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Validity Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                      <CircularProgress size={40} sx={{ color: accent }} />
                      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        Loading limits...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredLimits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                      <Box sx={{ 
                        display: 'inline-flex', 
                        p: 3, 
                        borderRadius: 3, 
                        backgroundColor: 'grey.100',
                        mb: 2
                      }}>
                        <AccountBalance sx={{ fontSize: 48, color: 'grey.400' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
                        No Limits Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No limits match your search criteria.' : 'No limits have been defined yet.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLimits.map((limit, index) => (
                    <TableRow 
                      key={limit.id} 
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: softBg,
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{limit.userId}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>{limit.userName}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: accent, fontSize: '0.8rem' }}>
                        {formatCurrency(limit.amount, limit.currency)}
                      </TableCell>
                      <TableCell sx={{ color: 'success.main', fontWeight: 700, fontSize: '0.8rem' }}>
                        {formatCurrency(limit.remainingAmount, limit.currency)}
                      </TableCell>
                      <TableCell sx={{ color: 'warning.main', fontWeight: 700, fontSize: '0.8rem' }}>
                        {formatCurrency(limit.amount - limit.remainingAmount, limit.currency)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={limit.currency} 
                          size="small" 
                          variant="outlined"
                          sx={{ borderRadius: 3, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {new Date(limit.validityDate).toLocaleDateString('en-US')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(limit.status)}
                          color={getStatusColor(limit.status)}
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            borderRadius: 3,
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit Limit" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleEditLimit(limit)}
                            sx={{
                              color: accent,
                              '&:hover': {
                                backgroundColor: softBg,
                              }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Edit Limit Dialog */}
        <Dialog
          open={editDialog.open}
          onClose={closeEditDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
            }
          }}
        >
          <DialogTitle sx={{ 
            background: accentGradient,
            color: contrast,
            p: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Edit sx={{ mr: 1.5, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Edit Limit
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ p: 4, mt: 2 }}>
            {editDialog.limit && (
              <Alert 
                severity="info" 
                icon={<Business />}
                sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'primary.main' }}
              >
                Updating limit for <strong>{editDialog.limit.userName}</strong> (ID: {editDialog.limit.userId})
              </Alert>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Digital Payment Limit"
                  value={editForm.amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  type="text"
                  required
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body1" sx={{ fontWeight: 600, color: editForm.amount ? accent : 'text.disabled' }}>
                          EUR
                        </Typography>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: accent,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: accent,
                      }
                    },
                    '& input': {
                      fontSize: '1.1rem',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth 
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: accent,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: accent,
                      }
                    }
                  }}
                >
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={editForm.currency}
                    onChange={(e) => handleEditFormChange('currency', e.target.value)}
                    label="Currency"
                    startAdornment={
                      <InputAdornment position="start">
                        <Euro color="primary" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="USD">USD - US Dollar</MenuItem>
                    <MenuItem value="EUR">EUR - Euro</MenuItem>
                    <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Deposit Account Number"
                  value={editForm.depositAccountNumber}
                  onChange={(e) => handleEditFormChange('depositAccountNumber', e.target.value)}
                  type="number"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalance color="primary" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: accent,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: accent,
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Validity Date"
                    value={editForm.validityDate}
                    onChange={(newValue) => handleEditFormChange('validityDate', newValue)}
                    minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        variant: 'outlined',
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarToday color={editForm.validityDate ? 'primary' : 'disabled'} />
                            </InputAdornment>
                          ),
                        },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: accent,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: accent,
                            },
                          }
                        }
                      },
                      popper: {
                        sx: {
                          '& .MuiPaper-root': {
                            borderRadius: 3,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          },
                          '& .MuiPickersDay-root': {
                            borderRadius: 3,
                            '&.Mui-selected': {
                              backgroundColor: accent,
                              '&:hover': {
                                backgroundColor: accentLight,
                              }
                            }
                          },
                          '& .MuiPickersCalendarHeader-root': {
                            '& .MuiPickersCalendarHeader-switchViewButton': {
                              color: accent,
                            },
                            '& .MuiPickersArrowSwitcher-button': {
                              color: accent,
                            }
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button 
              onClick={closeEditDialog}
              variant="outlined"
              sx={{
                borderColor: accent,
                color: accent,
                '&:hover': {
                  borderColor: accentLight,
                  backgroundColor: softBg,
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditSubmit} 
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                background: accentGradient,
                boxShadow: `0 4px 15px ${outline}`,
                '&:hover': {
                  background: accentGradientHover,
                  boxShadow: `0 6px 20px ${outline}`,
                }
              }}
            >
              Update Limit
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ViewLimits;